/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Scale, AlertTriangle, Mail } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-cyan-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <img src="/logo.jpg" alt="SkillDelivery" className="h-10" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Title Section */}
          <div className="text-center mb-8 pb-6 border-b border-gray-200">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-cyan-100 rounded-full">
                <FileText className="w-8 h-8 text-cyan-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Terms & Conditions</h1>
            <p className="text-gray-500">SkillDelivery Platform</p>
            <p className="text-sm text-gray-400 mt-2">Version 1.2 • Effective: December 2025</p>
          </div>

          {/* Quick Navigation */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            <h3 className="font-semibold text-gray-700 mb-3">Quick Navigation</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <a href="#acceptance" className="text-cyan-600 hover:underline">1. Acceptance</a>
              <a href="#accounts" className="text-cyan-600 hover:underline">2. User Accounts</a>
              <a href="#content" className="text-cyan-600 hover:underline">3. Content Policy</a>
              <a href="#violations" className="text-cyan-600 hover:underline">4. Violations</a>
              <a href="#privacy" className="text-cyan-600 hover:underline">5. Privacy</a>
              <a href="#intellectual" className="text-cyan-600 hover:underline">6. IP Rights</a>
              <a href="#termination" className="text-cyan-600 hover:underline">7. Termination</a>
              <a href="#contact" className="text-cyan-600 hover:underline">8. Contact</a>
            </div>
          </div>

          {/* Terms Content */}
          <div className="prose prose-gray max-w-none">
            {/* Section 1 */}
            <section id="acceptance" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <Scale className="w-5 h-5 text-cyan-600" />
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                By accessing or using the SkillDelivery platform ("Platform"), you agree to be bound by these
                Terms & Conditions ("Terms"). If you do not agree to these Terms, you may not access or use
                the Platform.
              </p>
              <p className="text-gray-600 leading-relaxed">
                SkillDelivery is operated by SKD RATWANI Technology & Software Solutions Private Limited
                ("Company", "we", "us", or "our"), a company registered in India.
              </p>
            </section>

            {/* Section 2 */}
            <section id="accounts" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <Shield className="w-5 h-5 text-cyan-600" />
                2. User Accounts
              </h2>
              <h3 className="font-semibold text-gray-700 mt-4 mb-2">2.1 Account Types</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li><strong>Individual:</strong> Personal learning accounts for individual users</li>
                <li><strong>Organization:</strong> Business accounts for corporate training</li>
                <li><strong>Institute:</strong> Educational institution accounts</li>
                <li><strong>University:</strong> Higher education institution accounts</li>
              </ul>

              <h3 className="font-semibold text-gray-700 mt-4 mb-2">2.2 Account Responsibilities</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>You must provide accurate and complete registration information</li>
                <li>You are responsible for maintaining the security of your account credentials</li>
                <li>You must notify us immediately of any unauthorized access</li>
                <li>You may not share your account with others or transfer your account</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="content" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <FileText className="w-5 h-5 text-cyan-600" />
                3. Content Policy
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                SkillDelivery is designed exclusively for professional skill development. All content
                uploaded, generated, or shared on the Platform must adhere to our content guidelines.
              </p>

              <h3 className="font-semibold text-gray-700 mt-4 mb-2">3.1 Permitted Content</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li>Professional training materials relevant to supported domains</li>
                <li>Educational content for skill development</li>
                <li>Technical documentation and learning resources</li>
                <li>Original or properly licensed content</li>
              </ul>

              <h3 className="font-semibold text-gray-700 mt-4 mb-2">3.2 Prohibited Content</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li><strong>Political content:</strong> Partisan, election-related, or politically charged material</li>
                  <li><strong>Biased content:</strong> Discriminatory or prejudiced material</li>
                  <li><strong>Harmful content:</strong> Dangerous, violent, or illegal information</li>
                  <li><strong>Misleading content:</strong> False, deceptive, or fraudulent information</li>
                  <li><strong>Off-domain content:</strong> Content unrelated to professional skill development</li>
                  <li><strong>IP violations:</strong> Copyrighted material without proper authorization</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section id="violations" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                4. Content Violations & Enforcement
              </h2>

              <h3 className="font-semibold text-gray-700 mt-4 mb-2">4.1 Three-Strike Policy</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We enforce a three-strike policy for content violations:
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="bg-amber-200 text-amber-800 text-xs font-bold px-2 py-1 rounded">Strike 1</span>
                    <span className="text-gray-600">Warning email sent, content flagged</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-orange-200 text-orange-800 text-xs font-bold px-2 py-1 rounded">Strike 2</span>
                    <span className="text-gray-600">Final warning, content removed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-red-200 text-red-800 text-xs font-bold px-2 py-1 rounded">Strike 3</span>
                    <span className="text-gray-600">Account suspended</span>
                  </li>
                </ul>
              </div>

              <h3 className="font-semibold text-gray-700 mt-4 mb-2">4.2 Appeal Process</h3>
              <p className="text-gray-600 leading-relaxed">
                You may appeal any violation within 7 days by contacting our legal team at{' '}
                <a href="mailto:legal@skilldelivery.co.in" className="text-cyan-600 hover:underline">
                  legal@skilldelivery.co.in
                </a>.
                Appeals are reviewed within 10 business days.
              </p>
            </section>

            {/* Section 5 */}
            <section id="privacy" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <Shield className="w-5 h-5 text-cyan-600" />
                5. Privacy & Data Protection
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Your privacy is important to us. By using the Platform, you consent to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Collection and processing of your registration information</li>
                <li>Storage of your learning progress and activity data</li>
                <li>Use of AI systems to analyze and improve your learning experience</li>
                <li>Communication via email for account-related matters</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                We do not sell your personal information to third parties. Data is stored securely
                and processed in accordance with applicable data protection laws.
              </p>
            </section>

            {/* Section 6 */}
            <section id="intellectual" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <Scale className="w-5 h-5 text-cyan-600" />
                6. Intellectual Property Rights
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Platform, including its design, features, and content (excluding user-generated content),
                is owned by SKD RATWANI Technology & Software Solutions Private Limited and protected by
                intellectual property laws.
              </p>
              <p className="text-gray-600 leading-relaxed">
                You retain ownership of content you upload, but grant us a license to use, display,
                and process such content for Platform operations.
              </p>
            </section>

            {/* Section 7 */}
            <section id="termination" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <AlertTriangle className="w-5 h-5 text-cyan-600" />
                7. Termination
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may suspend or terminate your account if you violate these Terms. Upon termination:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Your access to the Platform will be revoked</li>
                <li>Your data may be retained as required by law</li>
                <li>You may request export of your data within 30 days of termination</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section id="contact" className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-800 mb-4">
                <Mail className="w-5 h-5 text-cyan-600" />
                8. Contact Information
              </h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="font-semibold text-gray-800 mb-4">
                  SKD RATWANI Technology & Software Solutions Private Limited
                </p>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Registered Office:</strong> Sitapur, Uttar Pradesh, India - 261001</p>
                  <p><strong>Jurisdiction:</strong> Noida, Uttar Pradesh, India - 201301</p>
                  <p><strong>Legal Inquiries:</strong>{' '}
                    <a href="mailto:legal@skilldelivery.co.in" className="text-cyan-600 hover:underline">
                      legal@skilldelivery.co.in
                    </a>
                  </p>
                  <p><strong>Support:</strong>{' '}
                    <a href="mailto:support@skilldelivery.co.in" className="text-cyan-600 hover:underline">
                      support@skilldelivery.co.in
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              © 2025 SKD RATWANI Technology & Software Solutions Private Limited. All Rights Reserved.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Last updated: December 2025 • Version 1.2
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
